import type { UseSuspenseQueryOptions } from "@tanstack/react-query";
import type { ScheduledChangesResponse } from "@/app/billing/types";
import { useGetScheduledChanges, useSuspenseGetSubscription } from "./hooks";

export const hasScheduledChangeToRaw = (
  scheduledChanges: ScheduledChangesResponse | undefined,
  planId: string
) => {
  if (!Array.isArray(scheduledChanges) || scheduledChanges.length === 0) {
    return false;
  }

  const nextScheduledChange = scheduledChanges.find(
    (change) => change.end_date === null
  );

  return (
    nextScheduledChange && nextScheduledChange.plan.external_plan_id === planId
  );
};

export function useScheduledChangeHelpers(
  organizationId: string,
  options?: Omit<
    UseSuspenseQueryOptions<ScheduledChangesResponse>,
    "queryKey" | "queryFn"
  >
) {
  const { data: scheduledChanges } = useGetScheduledChanges(
    organizationId,
    options
  );
  const { data: subscription } = useSuspenseGetSubscription(organizationId);

  const scheduledChangesArray = Array.isArray(scheduledChanges)
    ? scheduledChanges
    : [];
  const nextScheduledChange = scheduledChangesArray.find(
    (change) => change.end_date === null
  );

  const hasScheduledChange =
    nextScheduledChange &&
    nextScheduledChange.plan.external_plan_id !==
      subscription?.plan?.external_plan_id;

  const hasScheduledChangeTo = (planId: string) =>
    hasScheduledChangeToRaw(scheduledChangesArray, planId);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  return {
    scheduledChanges,
    nextScheduledChange,
    hasScheduledChange,
    hasScheduledChangeTo,
    formatDate,
  };
}
